/**
 * Onboarding Wizard Page
 *
 * Multi-step wizard for new user setup:
 * - Step 0 (Admin only): Create user with email/name
 * - Step 1: Welcome/intro
 * - Step 2: Voice samples collection
 * - Step 3: Topic preferences
 * - Step 4: Generate voice profile & complete
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../platform';
import { apiRequest } from '../platform/api';
import { api } from '../lib/api';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  User,
  ThumbsUp,
  Tag,
  Check,
  Loader2,
  Plus,
  X,
  FileText,
  Link as LinkIcon,
} from 'lucide-react';

// Module-level callback for LinkedIn import — survives component remounts.
// When AppLayout re-renders (e.g. admin API calls completing), OnboardingPage can
// remount while a fetch is in flight. This ensures the result reaches the current instance.
let linkedInImportCallback: ((content: string) => void) | null = null;
let pendingLinkedInContent: string | null = null;

// Types
interface OnboardingStatus {
  completed: boolean;
  current_step: number;
  has_voice_profile: boolean;
  topics_include: string[];
  topics_exclude: string[];
  counts: {
    sources: number;
    voice_samples: number;
    knowledge_items: number;
  };
}

interface VoiceSample {
  content: string;
  type: string;
}

// Wizard steps
const STEPS = {
  CREATE_USER: 0, // Admin only
  WELCOME: 1,
  VOICE_SAMPLES: 2,
  TOPIC_PREFERENCES: 3,
  GENERATE_PROFILE: 4,
};

const STEP_LABELS = [
  'Create User',
  'Welcome',
  'LinkedIn Posts',
  'Topics',
  'Complete',
];

// Suggested topics
const SUGGESTED_TOPICS = [
  'Leadership',
  'Product Management',
  'Engineering',
  'AI & Machine Learning',
  'Startups',
  'Marketing',
  'Design',
  'Career Growth',
  'Remote Work',
  'Productivity',
  'Team Building',
  'Data Science',
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const isAdminMode = searchParams.get('admin') === 'true';
  const onboardMode = searchParams.get('mode'); // 'onboard' for existing user
  const targetUserId = searchParams.get('userId'); // User ID to onboard (admin only)

  const isAdminOnboard = isAdminMode && onboardMode === 'onboard' && targetUserId;
  const startStep = isAdminMode && !isAdminOnboard ? STEPS.CREATE_USER : STEPS.WELCOME;

  // Use sessionStorage to persist step across component remounts
  const SESSION_KEY = 'mazo-onboarding-current-step';

  // Read persisted step - call once during render
  const getPersistedStep = (): number => {
    if (isAdminMode) return startStep; // Don't use sessionStorage in admin mode
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= STEPS.WELCOME) {
          // Cap at GENERATE_PROFILE - don't reset to startStep if value is too high
          const capped = Math.min(parsed, STEPS.GENERATE_PROFILE);
          console.log('[Onboarding] Restored step from sessionStorage:', capped);
          return capped;
        }
      }
    } catch (e) {
      // sessionStorage not available
    }
    return startStep;
  };

  const [currentStep, setCurrentStepInternal] = useState(getPersistedStep);

  // Wrapper to persist step changes to sessionStorage
  const setCurrentStep = useCallback((step: number) => {
    // Cap step at GENERATE_PROFILE to prevent invalid states
    const cappedStep = Math.min(step, STEPS.GENERATE_PROFILE);

    console.log('[Onboarding] setCurrentStep called with:', step, '-> capped to:', cappedStep);

    // Persist to sessionStorage first
    if (!isAdminMode) {
      try {
        sessionStorage.setItem(SESSION_KEY, String(cappedStep));
        console.log('[Onboarding] Persisted to sessionStorage:', cappedStep);
      } catch (e) {
        console.error('[Onboarding] Failed to persist to sessionStorage:', e);
      }
    }

    // Update React state - this MUST trigger a re-render
    setCurrentStepInternal(cappedStep);
    console.log('[Onboarding] Called setCurrentStepInternal with:', cappedStep);
  }, [isAdminMode]);

  // Helper to clear all onboarding sessionStorage
  const clearPersistedStep = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(LOADED_KEY);
      sessionStorage.removeItem(PROFILE_GENERATED_KEY);
      sessionStorage.removeItem('mazo_voice_samples');
      sessionStorage.removeItem('mazo_import_result');
      console.log('[Onboarding] Cleared all onboarding sessionStorage');
    } catch (e) {
      // sessionStorage not available
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // sessionStorage keys for state persistence
  const LOADED_KEY = 'mazo-onboarding-loaded';
  const PROFILE_GENERATED_KEY = 'mazo-onboarding-profile-generated';

  // Helper functions for sessionStorage-based state
  const hasLoaded = () => sessionStorage.getItem(LOADED_KEY) === 'true';
  const setHasLoaded = (value: boolean) => {
    if (value) {
      sessionStorage.setItem(LOADED_KEY, 'true');
    } else {
      sessionStorage.removeItem(LOADED_KEY);
    }
  };
  const getProfileGenerated = () => sessionStorage.getItem(PROFILE_GENERATED_KEY) === 'true';
  const persistProfileGenerated = (value: boolean) => {
    if (value) {
      sessionStorage.setItem(PROFILE_GENERATED_KEY, 'true');
    } else {
      sessionStorage.removeItem(PROFILE_GENERATED_KEY);
    }
  };

  // Step 0: Create User (Admin only)
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);

  // Admin onboarding settings
  const [overrideExisting, setOverrideExisting] = useState(false);

  // Step 2: Voice Samples / LinkedIn Posts — persist in sessionStorage to survive auth-triggered remounts
  const [voiceSamples, setVoiceSamplesRaw] = useState<VoiceSample[]>(() => {
    try {
      const saved = sessionStorage.getItem('mazo_voice_samples');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return [];
  });
  const setVoiceSamples: typeof setVoiceSamplesRaw = (action) => {
    setVoiceSamplesRaw(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      try { sessionStorage.setItem('mazo_voice_samples', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const [linkedInUrls, setLinkedInUrls] = useState<string[]>(['']);
  const [importingUrls, setImportingUrls] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number } | null>(() => {
    try {
      const saved = sessionStorage.getItem('mazo_import_result');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { imported: parsed.imported, failed: parsed.failed };
      }
    } catch { /* ignore */ }
    return null;
  });
  const [importErrors, setImportErrors] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem('mazo_import_result');
      if (saved) return JSON.parse(saved).errors || [];
    } catch { /* ignore */ }
    return [];
  });

  // Register callback for LinkedIn import results — survives component remounts
  useEffect(() => {
    linkedInImportCallback = (content: string) => {
      setVoiceSamples(prev => {
        if (prev.length >= 10) return prev;
        return [...prev, { content, type: 'writing_sample' }];
      });
    };
    // Check for content that arrived during unmount-remount gap
    if (pendingLinkedInContent) {
      const content = pendingLinkedInContent;
      pendingLinkedInContent = null;
      linkedInImportCallback(content);
    }
    return () => { linkedInImportCallback = null; };
  });

  // Step 3: Topic Preferences
  const [topicsInclude, setTopicsInclude] = useState<string[]>([]);
  const [topicsExclude, setTopicsExclude] = useState<string[]>([]);
  const [customTopic, setCustomTopic] = useState('');

  // Step 4: Generate Profile
  const [generatingProfile, setGeneratingProfile] = useState(false);
  const [voiceProfile, setVoiceProfile] = useState<Record<string, unknown> | null>(null);
  // Track profileGenerated in state, initialized from sessionStorage
  const [profileGeneratedState, setProfileGeneratedState] = useState(getProfileGenerated);

  // Poll sessionStorage for changes (handles the case where async callback updates
  // sessionStorage but the component remounted so the state setter is stale)
  useEffect(() => {
    if (currentStep === STEPS.GENERATE_PROFILE && !profileGeneratedState) {
      const checkInterval = setInterval(() => {
        const currentValue = getProfileGenerated();
        if (currentValue && !profileGeneratedState) {
          console.log('[Onboarding] Detected profileGenerated change in sessionStorage');
          setProfileGeneratedState(true);
        }
      }, 500);
      return () => clearInterval(checkInterval);
    }
  }, [currentStep, profileGeneratedState]);

  // Use the state value for rendering
  const profileGenerated = profileGeneratedState;

  // Function to mark profile as generated - updates both sessionStorage and state
  const markProfileGenerated = useCallback(() => {
    console.log('[Onboarding] markProfileGenerated called');
    persistProfileGenerated(true);
    setProfileGeneratedState(true);
  }, []);

  // Load onboarding status ONCE on mount - use sessionStorage to survive remounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only load once, and only when authenticated
    if (!isAuthenticated || hasLoaded()) {
      // CRITICAL: Always set initialLoading to false, even if we're skipping the load
      // Otherwise on remount, initialLoading stays true and UI shows loading spinner
      setInitialLoading(false);
      return;
    }

    setHasLoaded(true);
    console.log('[Onboarding] Loading status (first time only)');

    const loadStatus = async () => {
      try {
        const result = await apiRequest<{ data: OnboardingStatus }>('/api/onboarding/status');
        const status = result.data;

        if (!status) {
          setInitialLoading(false);
          return;
        }

        // If already completed, redirect (unless admin mode)
        if (status.completed && !isAdminMode) {
          // Check if handleComplete set a redirect target before the remount
          const redirectTarget = sessionStorage.getItem('mazo_onboarding_redirect');
          sessionStorage.removeItem('mazo_onboarding_redirect');
          clearPersistedStep();
          navigate(redirectTarget || '/');
          return;
        }

        // Restore state from server ONLY if server step is ahead of local/sessionStorage step
        // This prevents the server from overwriting user navigation
        if (status.current_step > 0 && !isAdminMode) {
          const localStep = getPersistedStep();
          const serverStep = Math.min(status.current_step, STEPS.GENERATE_PROFILE);
          // Only update if server is ahead
          if (serverStep > localStep) {
            console.log('[Onboarding] Server step ahead, updating:', serverStep, '> local:', localStep);
            setCurrentStep(serverStep);
          } else {
            console.log('[Onboarding] Local step preserved:', localStep, '>= server:', serverStep);
          }
        }
        if (status.has_voice_profile) {
          persistProfileGenerated(true);
        }
        if (status.topics_include.length > 0) {
          setTopicsInclude(status.topics_include);
        }
        if (status.topics_exclude.length > 0) {
          setTopicsExclude(status.topics_exclude);
        }
      } catch (err) {
        console.error('Failed to load onboarding status:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    loadStatus();
  }, [isAuthenticated]); // Only depend on isAuthenticated - other values are accessed via refs/closures

  const updateStep = useCallback(async (step: number) => {
    try {
      await apiRequest('/api/onboarding/step', {
        method: 'POST',
        body: { step },
      });
    } catch (err) {
      console.error('Failed to update step:', err);
    }
  }, []);

  // Use a ref to always have the latest step value (avoids closure issues)
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;

  const handleNext = async (e?: React.MouseEvent) => {
    // Prevent event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Get the ACTUAL current step from ref (not closure)
    const stepToProcess = currentStepRef.current;

    console.log('[Onboarding] handleNext CLICKED - stepToProcess:', stepToProcess, 'loading:', loading);

    // Simple guard - just use loading state
    if (loading) {
      console.log('[Onboarding] handleNext blocked - already loading');
      return;
    }

    // Don't advance past the final step
    if (stepToProcess >= STEPS.GENERATE_PROFILE) {
      console.log('[Onboarding] handleNext blocked - already at final step:', stepToProcess);
      return;
    }

    console.log('[Onboarding] handleNext STARTING - processing step:', stepToProcess);

    // Set loading FIRST to prevent double-clicks
    setLoading(true);
    setError(null);

    // Calculate next step IMMEDIATELY before any async work
    const nextStep = Math.min(stepToProcess + 1, STEPS.GENERATE_PROFILE);

    try {
      // Validate current step before proceeding
      if (stepToProcess === STEPS.CREATE_USER && isAdminMode) {
        if (!newUserEmail || !newUserName) {
          setError('Please enter email and name for the new user');
          setLoading(false);
          return;
        }

        // Create user via admin API
        const result = await apiRequest<{
          data: {
            user_id: string;
            email: string;
            name: string;
            temporary_password?: string;
          };
        }>('/api/admin/users', {
          method: 'POST',
          body: {
            email: newUserEmail,
            name: newUserName,
            is_admin: false,
          },
        });

        console.log('User created successfully:', result.data);
        setCreatedUserId(result.data.user_id);

        if (result.data.temporary_password) {
          alert(
            `User created successfully!\n\n` +
            `Email: ${result.data.email}\n` +
            `Temporary Password: ${result.data.temporary_password}\n\n` +
            `Please save this password - it won't be shown again.`
          );
        }
      }

      if (stepToProcess === STEPS.VOICE_SAMPLES) {
        const validSamples = voiceSamples.filter(s => s.content.trim().length >= 100);
        if (validSamples.length === 0) {
          setError('Please add at least one writing sample (minimum 100 characters)');
          setLoading(false);
          return;
        }

        // Submit voice samples — must succeed before advancing (needed for voice generation)
        if (!isAdminOnboard) {
          try {
            await apiRequest('/api/onboarding/voice-samples', {
              method: 'POST',
              body: { samples: validSamples },
            });
          } catch (err) {
            console.error('Failed to save voice samples:', err);
            // Parse error for a user-friendly message
            let errorMessage = 'Failed to save voice samples. Please try again.';
            if (err instanceof Error) {
              try {
                const parsed = JSON.parse(err.message);
                if (parsed?.error?.message) errorMessage = parsed.error.message;
              } catch {
                // If it's not JSON, use the raw message only if it's readable
                if (err.message && err.message.length < 200) errorMessage = err.message;
              }
            }
            setError(errorMessage);
            setLoading(false);
            return;
          }
        }
      }

      if (stepToProcess === STEPS.TOPIC_PREFERENCES) {
        // If no topics selected, use sensible defaults so the user always has topic preferences
        const effectiveTopicsInclude = topicsInclude.length > 0
          ? topicsInclude
          : ['Leadership', 'Professional Growth', 'Industry Insights'];

        // Submit topic preferences — must succeed before advancing
        if (!isAdminOnboard) {
          try {
            await apiRequest('/api/onboarding/topics', {
              method: 'POST',
              body: {
                topics_include: effectiveTopicsInclude,
                topics_exclude: topicsExclude,
              },
            });
            // Update local state to reflect defaults if they were applied
            if (topicsInclude.length === 0) {
              setTopicsInclude(effectiveTopicsInclude);
            }
          } catch (err) {
            console.error('Failed to save topics:', err);
            setError('Failed to save topic preferences. Please try again.');
            setLoading(false);
            return;
          }
        }
      }

      console.log('[Onboarding] ADVANCING from step', stepToProcess, 'to step', nextStep);

      // Update step - this will also persist to sessionStorage
      setCurrentStep(nextStep);

      // Update step on server in background
      void updateStep(nextStep);

      console.log('[Onboarding] handleNext COMPLETE');
    } catch (err) {
      console.error('[Onboarding] handleNext error:', err);
      // Parse error for a user-friendly message
      let errorMessage = 'An error occurred. Please try again.';
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed?.error?.message) errorMessage = parsed.error.message;
        } catch {
          if (err.message && err.message.length < 200) errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > startStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGenerateProfile = async () => {
    // DEBUG: This alert proves the new code is loaded
    console.log('[Onboarding] === BUTTON CLICKED - NEW CODE v2 ===');

    // Check if already generated (persisted in sessionStorage)
    if (getProfileGenerated()) {
      console.log('[Onboarding] handleGenerateProfile blocked - already generated');
      // Clear the flag and try again
      persistProfileGenerated(false);
      setProfileGeneratedState(false);
    }

    // Prevent duplicate API calls
    if (generatingProfile) {
      console.log('[Onboarding] handleGenerateProfile blocked - already generating');
      return;
    }

    console.log('[Onboarding] Setting generatingProfile to TRUE');
    // Use flushSync to force React to commit the update to DOM
    flushSync(() => {
      setGeneratingProfile(true);
      setError(null);
    });

    // CRITICAL: Give browser time to actually PAINT the overlay
    // flushSync commits to DOM, but browser needs a frame to paint
    await new Promise(resolve => requestAnimationFrame(() => {
      console.log('[Onboarding] Browser painted, overlay should now be visible');
      resolve(undefined);
    }));
    console.log('[Onboarding] handleGenerateProfile STARTING - proceeding with API call');

    try {
      // In admin onboard mode, call the admin simulation endpoint
      if (isAdminOnboard && targetUserId) {
        const validSamples = voiceSamples.filter(s => s.content.trim().length >= 100);
        const result = await apiRequest<{ data: { voice_profile: Record<string, unknown> } }>(
          `/api/admin/users/${targetUserId}/onboard`,
          {
            method: 'POST',
            body: {
              voice_samples: validSamples.map(s => s.content),
              topics_include: topicsInclude,
              topics_exclude: topicsExclude,
              override_existing: overrideExisting,
            },
          }
        );
        console.log('[Onboarding] Voice profile generated (admin):', result.data);
        setVoiceProfile(result.data.voice_profile);
        markProfileGenerated();
      } else {
        // Regular mode or admin create mode: call regular endpoint
        const result = await apiRequest<{ data: { voice_profile: Record<string, unknown> } }>(
          '/api/onboarding/generate-voice',
          {
            method: 'POST',
          }
        );
        console.log('[Onboarding] Voice profile generated:', result.data);
        setVoiceProfile(result.data.voice_profile);
        markProfileGenerated();
      }
      console.log('[Onboarding] handleGenerateProfile COMPLETE - profileGenerated set to true');
    } catch (err) {
      console.error('[Onboarding] handleGenerateProfile error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate voice profile');
    } finally {
      setGeneratingProfile(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // In admin onboard mode, just navigate back to admin users
      if (isAdminOnboard && targetUserId) {
        clearPersistedStep();
        navigate(`/admin/users/${targetUserId}/edit`);
      } else {
        // Set redirect target BEFORE the API call — if auth refresh causes a remount,
        // the new instance's loadStatus will find this and redirect correctly
        const redirectTarget = (isAdminMode && createdUserId)
          ? `/admin/users/${createdUserId}/edit`
          : '/sources?connect=true';
        sessionStorage.setItem('mazo_onboarding_redirect', redirectTarget);

        // Regular mode or admin create mode: complete onboarding
        await apiRequest('/api/onboarding/complete', { method: 'POST' });

        // Clear all onboarding state AFTER the API call completes
        clearPersistedStep();
        navigate(redirectTarget);
      }
    } catch (err) {
      // Clean up redirect target on error
      sessionStorage.removeItem('mazo_onboarding_redirect');
      setError(err instanceof Error ? err.message : 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const addVoiceSample = () => {
    if (voiceSamples.length < 10) {
      setVoiceSamples([...voiceSamples, { content: '', type: 'writing_sample' }]);
    }
  };

  const handleImportAllLinkedInUrls = async () => {
    const validUrls = linkedInUrls.filter(u => u.trim());
    if (validUrls.length === 0 || importingUrls) return;

    setImportingUrls(true);
    setImportResult(null);
    setImportErrors([]);

    const results = await Promise.allSettled(
      validUrls.map(async (url) => {
        const result = await api.fetchLinkedInPost(url.trim());
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const content = result.data?.data?.content || (result.data as any)?.content;
        if (content && typeof content === 'string' && content.length > 0) {
          return content;
        }
        throw new Error(result.error?.message || 'Could not extract content');
      })
    );

    const imported: string[] = [];
    const errors: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        imported.push(r.value);
      } else {
        errors.push(`URL ${i + 1}: ${r.reason instanceof Error ? r.reason.message : 'Failed'}`);
      }
    });

    // Use module-level callback to add each imported post — survives auth-triggered remounts.
    // The closure's setVoiceSamples may be dead after remount, but linkedInImportCallback
    // always points to the CURRENT mounted instance.
    for (const content of imported) {
      if (linkedInImportCallback) {
        linkedInImportCallback(content);
      } else {
        pendingLinkedInContent = content;
      }
    }

    // Persist result in sessionStorage so feedback survives remount
    const resultData = { imported: imported.length, failed: errors.length, errors };
    sessionStorage.setItem('mazo_import_result', JSON.stringify(resultData));
    // Try to update local state (works if no remount happened)
    setImportResult({ imported: imported.length, failed: errors.length });
    setImportErrors(errors);
    setImportingUrls(false);
  };

  const removeVoiceSample = (index: number) => {
    setVoiceSamples(voiceSamples.filter((_, i) => i !== index));
  };

  const updateVoiceSample = (index: number, content: string) => {
    setVoiceSamples(voiceSamples.map((s, i) => i === index ? { ...s, content } : s));
  };

  const toggleTopic = (topic: string, type: 'include' | 'exclude') => {
    if (type === 'include') {
      if (topicsInclude.includes(topic)) {
        setTopicsInclude(topicsInclude.filter(t => t !== topic));
      } else {
        setTopicsInclude([...topicsInclude, topic]);
        setTopicsExclude(topicsExclude.filter(t => t !== topic));
      }
    } else {
      if (topicsExclude.includes(topic)) {
        setTopicsExclude(topicsExclude.filter(t => t !== topic));
      } else {
        setTopicsExclude([...topicsExclude, topic]);
        setTopicsInclude(topicsInclude.filter(t => t !== topic));
      }
    }
  };

  const addCustomTopic = () => {
    if (customTopic.trim() && !topicsInclude.includes(customTopic.trim())) {
      setTopicsInclude([...topicsInclude, customTopic.trim()]);
      setCustomTopic('');
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.skipOnboarding();
      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }
      // Clear sessionStorage on skip
      clearPersistedStep();
      // Navigate to sources after skipping
      navigate('/sources');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip onboarding');
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    if (!confirm('This will reset all your onboarding progress including voice samples and topics. Continue?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // CRITICAL: Reset server-side data first (voice samples, topics, voice profile)
      await api.resetOnboarding();

      // Clear all sessionStorage
      clearPersistedStep();

      // Reset all local state
      setCurrentStepInternal(startStep);
      setVoiceSamples([]);
      setLinkedInUrls(['']);
      setImportResult(null);
      setImportErrors([]);
      setTopicsInclude([]);
      setTopicsExclude([]);
      setCustomTopic('');
      // profileGenerated is cleared via clearPersistedStep() above
      setVoiceProfile(null);
      setGeneratingProfile(false);
      setLoading(false);

      // Force page reload to ensure completely clean state
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset onboarding');
      setLoading(false);
    }
  };

  // Use React state directly for rendering
  // Debug logging to trace render
  console.log('[Onboarding] RENDER - currentStep:', currentStep, 'loading:', loading, 'profileGenerated:', profileGenerated);

  // Next button should only show if we're before the final step
  const showNextButton = currentStep < STEPS.GENERATE_PROFILE;

  // Calculate visible steps (skip CREATE_USER if not admin)
  const visibleSteps = isAdminMode
    ? STEP_LABELS
    : STEP_LABELS.slice(1);

  const visibleStepIndex = isAdminMode ? currentStep : currentStep - 1;

  // Show loading spinner while fetching initial status
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-mazo-400 mx-auto" />
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Loading overlay rendered via portal to ensure it's above EVERYTHING
  const loadingOverlay = generatingProfile ? createPortal(
    <div
      id="generating-profile-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
      }}
    >
      <div className="text-center space-y-6">
        <div className="relative mx-auto w-24 h-24">
          <div className="absolute inset-0 rounded-full border-4 border-mazo-200 animate-pulse"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-mazo-500 animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="p-4 bg-gradient-to-br from-mazo-400 to-mazo-600 rounded-full shadow-lg">
              <img src="/logo-white.svg" alt="" className="h-8 w-8 animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-gray-900">Creating Your Voice Profile</h3>
          <p className="text-gray-600 max-w-sm mx-auto">
            Our AI is analyzing your writing samples to understand your unique style, tone, and vocabulary...
          </p>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm text-mazo-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="animate-pulse">Analyzing patterns and tone...</span>
        </div>
        <div className="flex justify-center gap-2 pt-2">
          <div className="w-2 h-2 rounded-full bg-mazo-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-mazo-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-mazo-300 animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {loadingOverlay}
      <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo.svg" alt="Mazo" className="mx-auto mb-4 h-12 w-12" />
          <h1 className="text-2xl font-medium text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isAdminOnboard
              ? 'Onboard Existing User'
              : isAdminMode
                ? 'Set Up New User'
                : 'Welcome to Mazo'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isAdminOnboard
              ? 'Simulate onboarding for an existing user'
              : isAdminMode
                ? "Configure a new user's profile and preferences"
                : "Let's set up your content creation profile"}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {visibleSteps.map((label, index) => {
            const isActive = index === visibleStepIndex;
            const isCompleted = index < visibleStepIndex;
            return (
              <div key={label} className="flex items-center">
                <div
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                    ${isCompleted ? 'bg-mazo-500 text-white' : ''}
                    ${isActive ? 'bg-mazo-400 text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-500' : ''}
                  `}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                <span className={`ml-2 text-sm ${isActive ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {label}
                </span>
                {index < visibleSteps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-3 ${index < visibleStepIndex ? 'bg-mazo-500' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
            ref={(el) => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
          >
            {error}
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Step 0: Create User (Admin only) */}
          {currentStep === STEPS.CREATE_USER && isAdminMode && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-mazo-100 rounded-lg">
                  <User className="h-5 w-5 text-mazo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Create User Account</h2>
                  <p className="text-sm text-gray-500">Enter the new user's details</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                  />
                </div>
              </div>

              <p className="text-sm text-gray-500 bg-mazo-50 p-3 rounded-lg">
                After creating the account, you'll configure their voice profile and topic preferences.
                The user will be able to sign in with their email.
              </p>
            </div>
          )}

          {/* Step 1: Welcome */}
          {currentStep === STEPS.WELCOME && (
            <div className="text-center py-8" data-step="WELCOME">
              <div className="p-4 bg-gradient-to-br from-mazo-50 to-mazo-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <img src="/logo.svg" alt="Mazo" className="h-10 w-10" />
              </div>
              <h2 className="text-xl font-semibold mb-3">Build Your Voice</h2>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                Mazo learns your unique writing style to create content that sounds authentically like you.
                In the next steps, you'll provide some writing samples and choose your topics.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center max-w-md mx-auto">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <FileText className="h-6 w-6 text-mazo-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-600">Add writing samples</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Tag className="h-6 w-6 text-mazo-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-600">Choose topics</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <img src="/logo.svg" alt="Mazo" className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-xs text-gray-600">Generate voice</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Voice Samples */}
          {currentStep === STEPS.VOICE_SAMPLES && (
            <div className="space-y-6" data-step="VOICE_SAMPLES">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-mazo-100 rounded-lg">
                  <ThumbsUp className="h-5 w-5 text-mazo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">LinkedIn Posts I Like</h2>
                  <p className="text-sm text-gray-500">
                    Import posts by URL or paste text directly — up to 10 examples
                  </p>
                </div>
              </div>

              {/* Import from LinkedIn URLs */}
              <div className="p-4 bg-mazo-50 border border-mazo-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-mazo-600" />
                  <p className="text-sm font-medium text-mazo-900">Import from LinkedIn</p>
                </div>

                {linkedInUrls.map((url, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => {
                        const updated = [...linkedInUrls];
                        updated[index] = e.target.value;
                        setLinkedInUrls(updated);
                      }}
                      placeholder="https://linkedin.com/posts/..."
                      disabled={importingUrls}
                      className="flex-1 px-3 py-2 border border-mazo-300 rounded-lg text-sm focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500 bg-white disabled:opacity-50"
                    />
                    {linkedInUrls.length > 1 && (
                      <button
                        onClick={() => setLinkedInUrls(prev => prev.filter((_, i) => i !== index))}
                        disabled={importingUrls}
                        className="p-2 text-gray-400 hover:text-red-500 rounded-lg disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex items-center justify-between">
                  {linkedInUrls.length < 5 ? (
                    <button
                      onClick={() => setLinkedInUrls(prev => [...prev, ''])}
                      disabled={importingUrls}
                      className="flex items-center gap-1.5 text-xs text-mazo-500 hover:text-mazo-600 disabled:opacity-50"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add another URL
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">Maximum 5 URLs</span>
                  )}

                  <button
                    onClick={handleImportAllLinkedInUrls}
                    disabled={importingUrls || linkedInUrls.every(u => !u.trim())}
                    className="flex items-center gap-2 px-4 py-2 bg-mazo-500 text-white text-sm rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importingUrls ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <LinkIcon className="h-4 w-4" />
                        Import {linkedInUrls.filter(u => u.trim()).length === 1 ? 'Post' : `All ${linkedInUrls.filter(u => u.trim()).length} Posts`}
                      </>
                    )}
                  </button>
                </div>

                {/* Import result feedback */}
                {importResult && (
                  <div className={`text-xs p-2 rounded ${importResult.failed > 0 ? 'bg-amber-50 text-amber-700' : 'bg-mazo-50 text-mazo-700'}`}>
                    {importResult.imported > 0 && <>{importResult.imported} post{importResult.imported !== 1 ? 's' : ''} imported successfully. </>}
                    {importResult.failed > 0 && <>{importResult.failed} failed — try pasting those manually below.</>}
                  </div>
                )}
                {importErrors.length > 0 && (
                  <div className="text-xs text-red-600 space-y-0.5">
                    {importErrors.map((err, i) => <p key={i}>{err}</p>)}
                  </div>
                )}

                <p className="text-xs text-mazo-700">
                  Paste LinkedIn post links and click Import. The post text will appear as examples below.
                </p>
              </div>

              {/* Examples section */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Writing Examples {voiceSamples.length > 0 && `(${voiceSamples.length})`}
                </h3>
                {voiceSamples.length < 10 && (
                  <button
                    onClick={addVoiceSample}
                    className="flex items-center gap-1.5 text-xs text-mazo-500 hover:text-mazo-600"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add manually
                  </button>
                )}
              </div>

              {voiceSamples.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">
                    Import LinkedIn posts above or{' '}
                    <button onClick={addVoiceSample} className="text-mazo-500 hover:underline">
                      add an example manually
                    </button>
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {voiceSamples.map((sample, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-500">Example {index + 1}</span>
                        <button
                          onClick={() => removeVoiceSample(index)}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </button>
                      </div>
                      <textarea
                        value={sample.content}
                        onChange={(e) => updateVoiceSample(index, e.target.value)}
                        placeholder="Paste a LinkedIn post you like here (at least 100 characters)..."
                        rows={4}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 resize-none ${
                          sample.content.length >= 100 ? 'border-mazo-300' : 'border-gray-300'
                        }`}
                      />
                      <span className={`text-xs ${sample.content.length >= 100 ? 'text-mazo-600' : 'text-gray-400'}`}>
                        {sample.content.length} / 100+ characters
                        {sample.content.length >= 100 && <Check className="h-3 w-3 inline ml-1" />}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                The more examples you provide, the better Mazo can match your style. Aim for 2-3 diverse examples.
              </p>
            </div>
          )}

          {/* Step 3: Topic Preferences */}
          {currentStep === STEPS.TOPIC_PREFERENCES && (
            <div className="space-y-6" data-step="TOPIC_PREFERENCES">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-mazo-100 rounded-lg">
                  <Tag className="h-5 w-5 text-mazo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Topic Preferences</h2>
                  <p className="text-sm text-gray-500">
                    Select topics you want to write about (purple) or avoid (red)
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suggested Topics
                </label>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED_TOPICS.map(topic => {
                    const isIncluded = topicsInclude.includes(topic);
                    const isExcluded = topicsExclude.includes(topic);
                    return (
                      <button
                        key={topic}
                        onClick={() => toggleTopic(topic, isIncluded ? 'exclude' : 'include')}
                        className={`
                          px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                          ${isIncluded ? 'bg-mazo-100 text-mazo-700 border-2 border-mazo-400' : ''}
                          ${isExcluded ? 'bg-red-100 text-red-700 border-2 border-red-400' : ''}
                          ${!isIncluded && !isExcluded ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : ''}
                        `}
                      >
                        {topic}
                        {isIncluded && <Check className="h-3 w-3 inline ml-1" />}
                        {isExcluded && <X className="h-3 w-3 inline ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Custom Topic
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTopic}
                    onChange={(e) => setCustomTopic(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomTopic()}
                    placeholder="Enter a custom topic..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400"
                  />
                  <button
                    onClick={addCustomTopic}
                    disabled={!customTopic.trim()}
                    className="px-4 py-2 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </div>

              {topicsInclude.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Selected Topics ({topicsInclude.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {topicsInclude.map(topic => (
                      <span
                        key={topic}
                        className="px-3 py-1.5 bg-mazo-100 text-mazo-700 rounded-full text-sm font-medium flex items-center gap-1"
                      >
                        {topic}
                        <button onClick={() => toggleTopic(topic, 'include')}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Generate Profile */}
          {currentStep === STEPS.GENERATE_PROFILE && (
            <div className="space-y-6" data-step="GENERATE_PROFILE">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-mazo-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-mazo-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Generate Your Voice Profile</h2>
                  <p className="text-sm text-gray-500">
                    We'll analyze your samples to create a personalized writing profile
                  </p>
                </div>
              </div>

              {!profileGenerated ? (
                generatingProfile ? (
                  // Beautiful loading state while AI generates the profile
                  <div className="text-center py-12 space-y-6">
                    <div className="relative mx-auto w-24 h-24">
                      {/* Outer rotating ring */}
                      <div className="absolute inset-0 rounded-full border-4 border-mazo-200 animate-pulse"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-mazo-500 animate-spin"></div>
                      {/* Inner icon */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="p-4 bg-gradient-to-br from-mazo-400 to-mazo-600 rounded-full shadow-lg">
                          <img src="/logo-white.svg" alt="" className="h-8 w-8 animate-pulse" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold text-gray-900">
                        Creating Your Voice Profile
                      </h3>
                      <p className="text-gray-600 max-w-sm mx-auto">
                        Our AI is analyzing your writing samples to understand your unique style, tone, and vocabulary...
                      </p>
                    </div>

                    {/* Animated status messages */}
                    <div className="flex items-center justify-center gap-2 text-sm text-mazo-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="animate-pulse">Analyzing patterns and tone...</span>
                    </div>

                    {/* Progress dots */}
                    <div className="flex justify-center gap-2 pt-4">
                      <div className="w-2 h-2 rounded-full bg-mazo-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 rounded-full bg-mazo-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 rounded-full bg-mazo-300 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                ) : (
                  // Initial state - show generate button
                  <div className="text-center py-8 space-y-4">
                    {/* Override toggle - only in admin onboard mode */}
                    {isAdminOnboard && (
                      <div className="flex items-center justify-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <label htmlFor="override-toggle" className="flex items-center gap-3 cursor-pointer">
                          <input
                            id="override-toggle"
                            type="checkbox"
                            checked={overrideExisting}
                            onChange={(e) => setOverrideExisting(e.target.checked)}
                            className="w-4 h-4 text-mazo-600 bg-gray-100 border-gray-300 rounded focus:ring-mazo-500 focus:ring-2"
                          />
                          <div className="text-left">
                            <div className="text-sm font-medium text-amber-900">
                              Override existing settings
                            </div>
                            <div className="text-xs text-amber-700">
                              Replace current voice samples and topics instead of appending
                            </div>
                          </div>
                        </label>
                      </div>
                    )}

                    <div className="p-6 bg-gradient-to-br from-mazo-50 to-mazo-100 rounded-xl">
                      <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                        Ready to create your personalized voice profile based on your writing samples and topic preferences.
                      </p>
                      <button
                        onClick={handleGenerateProfile}
                        disabled={generatingProfile}
                        className="px-8 py-4 bg-mazo-600 text-white rounded-xl font-medium hover:bg-mazo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto shadow-lg hover:shadow-xl transition-all"
                      >
                        Generate Voice Profile
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      This typically takes 10-15 seconds
                    </p>
                  </div>
                )
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-mazo-50 border border-mazo-200 rounded-lg">
                    <div className="flex items-center gap-2 text-mazo-700 font-medium">
                      <Check className="h-5 w-5" />
                      Voice Profile Generated!
                    </div>
                  </div>

                  {voiceProfile && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium mb-3">Your Writing Style</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {'formality' in voiceProfile && voiceProfile.formality ? (
                          <div>
                            <span className="text-gray-500">Formality:</span>
                            <span className="ml-2 font-medium">{String(voiceProfile.formality)}</span>
                          </div>
                        ) : null}
                        {'narrativeStyle' in voiceProfile && voiceProfile.narrativeStyle ? (
                          <div>
                            <span className="text-gray-500">Style:</span>
                            <span className="ml-2 font-medium">{String(voiceProfile.narrativeStyle)}</span>
                          </div>
                        ) : null}
                        {'emotionalTone' in voiceProfile && voiceProfile.emotionalTone ? (
                          <div>
                            <span className="text-gray-500">Tone:</span>
                            <span className="ml-2 font-medium">{String(voiceProfile.emotionalTone)}</span>
                          </div>
                        ) : null}
                        {'sentenceStructure' in voiceProfile && voiceProfile.sentenceStructure ? (
                          <div>
                            <span className="text-gray-500">Sentences:</span>
                            <span className="ml-2 font-medium">{String(voiceProfile.sentenceStructure)}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <div className="text-center pt-4">
                    <button
                      onClick={handleComplete}
                      disabled={loading}
                      className="px-6 py-3 bg-mazo-600 text-white rounded-lg font-medium hover:bg-mazo-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          {isAdminMode ? 'Saving...' : 'Generating...'}
                        </>
                      ) : (
                        <>
                          <img src="/logo-white.svg" alt="" className="h-5 w-5" />
                          {isAdminMode ? 'Save & Return to User' : 'Generate My First LinkedIn Posts'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <button
            onClick={handleBack}
            disabled={currentStep === startStep || loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>

          {showNextButton && (
            <button
              type="button"
              onClick={(e) => handleNext(e)}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Skip for now link - only show for regular users, not admin mode */}
        {!isAdminMode && (
          <div className="text-center mt-6">
            <button
              onClick={handleSkip}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
            >
              Skip for now and use default settings
            </button>
            <p className="text-xs text-gray-400 mt-1">
              You can customize your voice profile later in Settings
            </p>
          </div>
        )}

        {/* Start Over link - always visible except on first step */}
        {currentStep > startStep && (
          <div className="text-center mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleStartOver}
              disabled={loading}
              className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
            >
              Having trouble? Start over from the beginning
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default OnboardingPage;
